import React from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import MapboxLocationSelector from "./MapboxLocationSelector";

// Days and default hours
const defaultOperatingHour = {
  enable: true,
  startTime: "09:00",
  endTime: "17:00",
};
const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const defaultData = {
  name: "",
  area: "",
  color: "",
  zipCode: "",
  timezone: "",
  text: "",
  notes: "",
  attachmentUrls: [],
  latitude: null,
  longitude: null,
  geofenceRadius: 100,
  usernameError: null,
  ...Object.fromEntries(
    daysOfWeek.map((day) => [day, { ...defaultOperatingHour }])
  ),
};


// Main EditGeneral component
const EditGeneral = ({ data, setData, companies = [], selectedCompany = null, onCompanyChange }) => {
  const user = useSelector((state) => state.user.user);
  const safeData = { ...defaultData, ...data };

  // Email validation function
  const validateEmail = (email, isRequired = false) => {
    if (!email || !email.trim()) {
      return !isRequired; // Empty is valid only if not required
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle simple field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      
      // Clear validation error when user starts typing
      if (name === "username" && prev.usernameError) {
        updated.usernameError = null;
      }
      
      return updated;
    });
  };

  // Handle username blur to validate email
  const handleUsernameBlur = (e) => {
    const email = e.target.value;
    const isRequired = safeData.enableLogin;
    
    if (!email || !email.trim()) {
      if (isRequired) {
        setData((prev) => ({
          ...prev,
          usernameError: "Email address is required when login is enabled",
        }));
      } else {
        setData((prev) => ({
          ...prev,
          usernameError: null,
        }));
      }
    } else if (!validateEmail(email, isRequired)) {
      setData((prev) => ({
        ...prev,
        usernameError: "Please enter a valid email address",
      }));
    } else {
      setData((prev) => ({
        ...prev,
        usernameError: null,
      }));
    }
  };

  // Handle nested operating hour changes
  const handleOperatingHourChange = (day, field, value) => {
    setData((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: field === "enable" ? value : value,
      },
    }));
  };

  // Handle file uploads
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const urls = files.map((file) => URL.createObjectURL(file));
    setData((prev) => ({
      ...prev,
      attachmentUrls: [...(prev.attachmentUrls || []), ...urls],
    }));
  };

  // Map interaction handlers
  const handleLocationChange = (lat, lng) => {
    if (lat !== null && lng !== null) {
      setData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));
    } else {
      setData((prev) => ({
        ...prev,
        latitude: null,
        longitude: null,
      }));
    }
  };

  const handleGeofenceChange = (radius) => {
    if (radius !== null) {
      setData((prev) => ({
        ...prev,
        geofenceRadius: Math.round(radius),
      }));
    } else {
      setData((prev) => ({
        ...prev,
        geofenceRadius: 100,
      }));
    }
  };

  const handleZipCodeChange = (zip) => {
    setData((prev) => ({
      ...prev,
      zipCode: zip,
    }));
  };

  // Example submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    // Process data here (e.g., send to API)
    // console.log(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basics */}
      <div className="bg-white px-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Basics</h2>
        <div className="grid grid-cols-2 gap-8">
          <div>
            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={safeData.name}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            {/* Area */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Area
              </label>
              <input
                type="text"
                name="area"
                value={safeData.area}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            {/* Company - Only for system administrators */}
            {user && user.deputyAccessLevel === "system_administrator" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <Select
                  name="company"
                  placeholder="Select a Company"
                  className="basic-multi-select w-full cursor-pointer"
                  classNamePrefix="select"
                  options={companies}
                  value={selectedCompany}
                  onChange={(option) => {
                    if (onCompanyChange) {
                      onCompanyChange(option);
                    }
                    setData((prev) => ({
                      ...prev,
                      companyID: option ? option._id : null,
                    }));
                  }}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option._id}
                  isClearable={true}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: "#d1d5db",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#9ca3af",
                      },
                    }),
                  }}
                />
              </div>
            )}
            {/* Area Color */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <input
                type="color"
                name="color"
                value={safeData.color}
                onChange={handleChange}
                className="h-10 w-16 p-0 border-gray-300 rounded cursor-pointer"
              />
            </div>
            {/* GPS Location */}
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              GPS Location
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <input
                type="number"
                step="any"
                name="latitude"
                value={safeData.latitude || ""}
                onChange={handleChange}
                placeholder="e.g., 53.3498"
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Decimal degrees (e.g., 53.3498 for Dublin)
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <input
                type="number"
                step="any"
                name="longitude"
                value={safeData.longitude || ""}
                onChange={handleChange}
                placeholder="e.g., -6.2603"
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Decimal degrees (e.g., -6.2603 for Dublin)
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geofence Radius (meters)
              </label>
              <input
                type="number"
                name="geofenceRadius"
                value={safeData.geofenceRadius || 100}
                onChange={handleChange}
                min="10"
                max="1000"
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Distance in meters for geofence validation (10-1000m)
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal code / ZIP code
              </label>
              <input
                type="text"
                name="zipCode"
                value={safeData.zipCode}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                rows={4}
                type="text"
                placeholder="Useful information that will appear in the location notes for every shift."
                name="notes"
                value={safeData.notes}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            
            {/* Login Credentials Section */}
            <div className="border-t pt-4 mt-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Login Credentials</h2>
              
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="enableLogin"
                    checked={safeData.enableLogin || false}
                    onChange={(e) => {
                      setData((prev) => ({
                        ...prev,
                        enableLogin: e.target.checked,
                        username: e.target.checked ? (prev.username || "") : "",
                        password: e.target.checked ? (prev.password || "") : "",
                      }));
                    }}
                    className="mr-2 w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable Login</span>
                </label>
              </div>

              {safeData.enableLogin && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location UserEmail <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="username"
                      value={safeData.username || ""}
                      onChange={handleChange}
                      onBlur={handleUsernameBlur}
                      required={safeData.enableLogin}
                      className={`w-full rounded border-gray-300 bg-slate-100 p-2 ${
                        safeData.usernameError
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "focus:ring-gray-500 focus:border-gray-500"
                      }`}
                      placeholder="Enter email address"
                    />
                    {safeData.usernameError && (
                      <p className="mt-1 text-sm text-red-600">{safeData.usernameError}</p>
                    )}
                  </div>

                  <div className="mb-4">
                    {safeData.showPasswordChange ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={safeData.password || ""}
                          onChange={handleChange}
                          className="w-full rounded border-gray-300 bg-slate-100 p-2"
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setData((prev) => ({
                              ...prev,
                              password: "",
                              showPasswordChange: false,
                            }));
                          }}
                          className="mt-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setData((prev) => ({
                            ...prev,
                            showPasswordChange: true,
                          }));
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition text-sm"
                      >
                        Password Change
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            {/* <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <input
                type="text"
                name="timezone"
                value={safeData.timezone}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div> */}
          </div>
          {/* Interactive Mapbox Map */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Location Map</h3>
            <MapboxLocationSelector
              latitude={safeData.latitude}
              longitude={safeData.longitude}
              geofenceRadius={safeData.geofenceRadius}
              zipCode={safeData.zipCode}
              onLocationChange={handleLocationChange}
              onGeofenceChange={handleGeofenceChange}
              onZipCodeChange={handleZipCodeChange}
            />
          </div>
        </div>
      </div>
      {/* Operating Hours */}
      <div className="bg-white p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Operating hours
        </h2>
        <div className="space-y-4">
          {daysOfWeek.map((day) => (
            <div key={day} className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 w-32">
                <input
                  type="checkbox"
                  checked={safeData[day].enable}
                  onChange={(e) =>
                    handleOperatingHourChange(day, "enable", e.target.checked)
                  }
                  className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700 capitalize">
                  {day}
                </label>
              </div>
              <input
                type="time"
                value={safeData[day].startTime}
                onChange={(e) =>
                  handleOperatingHourChange(day, "startTime", e.target.value)
                }
                disabled={!safeData[day].enable}
                className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-gray-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
              />
              <span className="text-gray-500">-</span>
              <input
                type="time"
                value={safeData[day].endTime}
                onChange={(e) =>
                  handleOperatingHourChange(day, "endTime", e.target.value)
                }
                disabled={!safeData[day].enable}
                className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-gray-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
          ))}
        </div>
      </div>
      {/* Notes */}

      {/* <div className="bg-white p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Notes</h2>
        <textarea
          name="text"
          value={safeData.text}
          onChange={handleChange}
          placeholder="Useful information that will appear in the location notes for every shift."
          className="w-full rounded border-gray-300 bg-slate-100 p-2 h-24"
        />
        <div className="mt-4 flex items-center space-x-4">
          <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
            <span className="text-sm text-gray-700">Attachment</span>
            <input
              type="file"
              multiple
              hidden
              onChange={handleFileChange}
              accept="image/jpeg,image/png,application/pdf"
            />
          </label>
          <span className="text-xs text-gray-500">
            Accepted file formats: JPEG, PNG, PDF. Max 5 MB, 10MB per file.
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {safeData.attachmentUrls?.map((url, idx) => (
            <a
              key={idx}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 underline"
            >
              Attachment {idx + 1}
            </a>
          ))}
        </div>
      </div> */}
    </form>
  );
};

export default EditGeneral;
